import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const IndustrySolutionsSection = () => {
  const [activeCategory, setActiveCategory] = useState('cardiovascular');

  const categories = [
  {
    id: 'cardiovascular',
    name: 'Cardiovascular',
    icon: 'Heart',
    description: 'Comprehensive monitoring for cardiac devices, stents, and vascular interventions',
    features: [
    'CMS National Coverage Determinations tracking',
    'State-specific cardiac device policies',
    'Reimbursement rate change alerts',
    'Clinical trial policy updates'],

    metrics: {
      policies: '12,500+',
      clients: '45',
      coverage: '98.7%'
    },
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13e1de3b8-1764646515688.png",
    imageAlt: 'Medical professional examining cardiovascular monitoring equipment with digital displays showing heart rate data in modern hospital setting'
  },
  {
    id: 'orthopedic',
    name: 'Orthopedic',
    icon: 'Bone',
    description: 'Specialized tracking for joint replacements, spinal devices, and orthopedic implants',
    features: [
    'Joint replacement coverage policies',
    'Spinal device reimbursement tracking',
    'Prior authorization requirement alerts',
    'Bundled payment policy monitoring'],

    metrics: {
      policies: '18,200+',
      clients: '52',
      coverage: '99.2%'
    },
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10bd88d75-1764792569958.png",
    imageAlt: 'Orthopedic surgeon examining knee joint replacement implant with surgical instruments on sterile table in operating room'
  },
  {
    id: 'surgical',
    name: 'Surgical Devices',
    icon: 'Scissors',
    description: 'Real-time monitoring for surgical instruments, robotics, and procedural equipment',
    features: [
    'Robotic surgery policy updates',
    'Minimally invasive procedure coverage',
    'Surgical device classification changes',
    'Hospital formulary policy tracking'],

    metrics: {
      policies: '15,800+',
      clients: '38',
      coverage: '97.5%'
    },
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16391f4a8-1766151683028.png",
    imageAlt: 'Surgical team performing minimally invasive procedure using robotic surgical system with multiple monitors in state-of-the-art operating theater'
  },
  {
    id: 'diagnostic',
    name: 'Diagnostic Imaging',
    icon: 'ScanLine',
    description: 'Policy intelligence for imaging equipment, contrast agents, and diagnostic tools',
    features: [
    'Imaging reimbursement policy tracking',
    'Contrast agent coverage updates',
    'Radiation safety regulation monitoring',
    'Diagnostic code change alerts'],

    metrics: {
      policies: '9,400+',
      clients: '29',
      coverage: '96.8%'
    },
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1210539b0-1767586961701.png",
    imageAlt: 'Radiologist analyzing diagnostic imaging scans on multiple high-resolution monitors in modern radiology department with advanced medical equipment'
  }];


  const activeData = categories?.find((cat) => cat?.id === activeCategory);

  return (
    <section className="bg-[#FAFBFC] py-12 md:py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 bg-[#1A73E8]/10 border border-[#1A73E8]/20 rounded-full px-4 py-2 mb-4">
            <Icon name="Layers" size={16} color="#1A73E8" />
            <span className="text-xs md:text-sm font-medium text-[#1A73E8]">Industry-Specific Solutions</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#1B1B20] mb-4">
            Tailored Intelligence for Your Device Category
          </h2>
          <p className="text-base md:text-lg text-[#6B7280] max-w-3xl mx-auto">
            Specialized monitoring solutions designed for the unique policy landscape of each medical device category
          </p>
        </div>

        <div className="flex flex-wrap gap-3 md:gap-4 justify-center mb-8 md:mb-12">
          {categories?.map((category) =>
          <button
            key={category?.id}
            onClick={() => setActiveCategory(category?.id)}
            className={`flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium transition-all ${
            activeCategory === category?.id ?
            'bg-[#1A73E8] text-white shadow-lg' :
            'bg-white text-[#6B7280] hover:bg-[#F8F9FA] border border-[#E5E7EB]'}`
            }>

              <Icon
              name={category?.icon}
              size={18}
              color={activeCategory === category?.id ? '#FFFFFF' : '#6B7280'} />

              <span className="text-sm md:text-base">{category?.name}</span>
            </button>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="p-6 md:p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-[#1A73E8]/10 rounded-xl flex items-center justify-center">
                  <Icon name={activeData?.icon} size={28} color="#1A73E8" />
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold text-[#1B1B20]">{activeData?.name}</h3>
                  <p className="text-sm text-[#6B7280]">Specialized Monitoring</p>
                </div>
              </div>

              <p className="text-base md:text-lg text-[#6B7280] mb-6 md:mb-8">
                {activeData?.description}
              </p>

              <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                {activeData?.features?.map((feature, index) =>
                <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#10B981]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon name="Check" size={14} color="#10B981" />
                    </div>
                    <span className="text-sm md:text-base text-[#1B1B20]">{feature}</span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4 md:gap-6 pt-6 border-t border-[#E5E7EB]">
                <div>
                  <div className="text-xl md:text-2xl font-bold text-[#1A73E8]">{activeData?.metrics?.policies}</div>
                  <div className="text-xs md:text-sm text-[#6B7280] mt-1">Policies Tracked</div>
                </div>
                <div>
                  <div className="text-xl md:text-2xl font-bold text-[#1A73E8]">{activeData?.metrics?.clients}</div>
                  <div className="text-xs md:text-sm text-[#6B7280] mt-1">Enterprise Clients</div>
                </div>
                <div>
                  <div className="text-xl md:text-2xl font-bold text-[#1A73E8]">{activeData?.metrics?.coverage}</div>
                  <div className="text-xs md:text-sm text-[#6B7280] mt-1">Coverage Rate</div>
                </div>
              </div>
            </div>

            <div className="relative h-64 lg:h-auto overflow-hidden">
              <Image
                src={activeData?.image}
                alt={activeData?.imageAlt}
                className="w-full h-full object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-[#1B1B20]/50 to-transparent"></div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default IndustrySolutionsSection;